import { Box, Chip, Link, Typography } from "@material-ui/core"
import { useMemo } from "react"
import { Link as ReactRouterLink } from 'react-router-dom'

import { DivisionMetadata, EventCourseMetadata } from "../../utils/types"

import { useDivisionsCardStyles, useSharedStyles } from "./styles"
import { FormattedEventEntry } from "./AthleteCard"
import { SkeletonPlaceholder } from "./SkeletonPlaceholder"

type DivisionItemProps = DivisionMetadata & {
  athletesCount: number
  rosterLink: string
}

const DivisionItem = ({
  athletesCount,
  awards,
  id,
  name,
  rosterLink,
}: DivisionItemProps) => {
  const styles = useDivisionsCardStyles()

  return (
    <Box className={styles.divisionItem}>
      <Box className={styles.divisionName}>
        <Typography>{name}</Typography>
      </Box>

      <Box>
        <Typography className={styles.divisionSectionTitle}>
          Athletes
        </Typography>

        <Typography className={styles.divisionAthletesCount}>
            {athletesCount.toLocaleString(navigator.language || 'en-US')}
        </Typography>
      </Box>

      {awards.depth > 0 && (
        <Box className={styles.divisionAwardsContainer}>
          <Typography className={styles.divisionSectionTitle}>
            Awards: Top {awards.depth}
          </Typography>
      
          <Box className={styles.rankTypeContainer}>
            <Typography className={styles.rankTypeLabel}>
              By
            </Typography>

            <Chip
              className={styles.rankTypeChip}
              label={awards.rankType === 'chip' ? 'Chip Time' : 'Gun Time'}
              size='small'
              variant='outlined'
            />
          </Box>
        </Box>
      )}

      <Box className={styles.divisionLink}>
        <Link
          to={`${rosterLink}/Division/${id}`}
          component={ReactRouterLink}
        >
          View Roster {'>'}
        </Link>
      </Box>
    </Box>
  )
}

type DivisionsCardProps = {
  azpEntry: FormattedEventEntry
  eventCourse: EventCourseMetadata | null
  eventId: number
  isLoadingEntry: boolean
  isLoadingEventCourse: boolean
  masterId: number | null
}

const DivisionsCard = ({
  azpEntry,
  eventCourse,
  eventId,
  isLoadingEntry,
  isLoadingEventCourse,
  masterId,
}: DivisionsCardProps) => {
  const styles = useDivisionsCardStyles()
  const sharedStyles = useSharedStyles()

  const mappedDivisions = useMemo(() => {
    if (!eventCourse) {
      return []
    }

    const entryDivisionIds = eventCourse?.metadata.divisions.reduce((acc: Omit<DivisionItemProps, 'rosterLink'>[], division) => {
      const azpEntryDivision = azpEntry.divisions.find((item) => item.id === division.id)

      if (azpEntryDivision) {
        acc.push({ ...division, athletesCount: azpEntryDivision.count })
      }

      return acc
    }, [])

    return entryDivisionIds
  }, [azpEntry, eventCourse])

  if (isLoadingEntry || isLoadingEventCourse) {
    return <SkeletonPlaceholder />
  }

  if (!eventCourse) {
    return null
  }

  return (
    <Box className={sharedStyles.paper}>
      <Typography className={styles.title}>Divisions ({mappedDivisions.length})</Typography>

      <Box className={styles.itemsContainer}>
        {mappedDivisions.map((division) => (
          <DivisionItem
            key={division.id}
            {...division}
            rosterLink={`/event/${masterId}/roster/Event/${eventId}/Course/${eventCourse.eventCourseId}`}
          />
        ))}
      </Box>
    </Box>
  )
}

export { DivisionsCard }